/**
 * ProcessingStep — per-section resume optimization with live typewriter.
 *
 * Each section gets its own focused LLM call (gpt-4o-mini via OpenAI).
 * All section calls fire concurrently; results are typed out in canonical order
 * as they arrive, so the user sees live progress for every section.
 */

import { motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, CheckCircle, Loader2, Sparkles, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';

import type { JobContext, ResumeContext } from '@soVendor/services/sectionOptimizationService';
import { optimizeSection } from '@soVendor/services/sectionOptimizationService';
import { normalizeResumeText } from '@soVendor/utils/pdfTextSanitizer';

import { parseResumeSections, sortSectionsByCanonicalOrder, normalizeSectionTitles } from './utils/parseResumeSections';
import type { ResumeSection } from './utils/parseResumeSections';
import { STEP_HEADER_ICON_CLASS, STEP_PRIMARY_CTA_CLASS } from './shared/stepLayout';
const TYPEWRITER_CHARS_PER_TICK = 4;
const TYPEWRITER_TICK_MS = 35;

// ── Known section headers to strip from LLM bleed ────────────────────────────
const PERSONAL_SECTION_TITLES = new Set([
  'PERSONAL INFORMATION', 'CONTACT INFORMATION', 'CONTACT', 'PERSONAL', 'NAME', 'HEADER',
]);

const KNOWN_SECTION_HEADERS = new Set([
  'PERSONAL INFORMATION','CONTACT INFORMATION','CONTACT','PROFESSIONAL','PROFESSIONAL SUMMARY',
  'SUMMARY','OBJECTIVE','WORK EXPERIENCE','PROFESSIONAL EXPERIENCE','EXPERIENCE','EMPLOYMENT',
  'EDUCATION','SKILLS','TECHNICAL SKILLS','KEY SKILLS','CERTIFICATIONS','CERTIFICATIONS & LICENSES',
  'LANGUAGES','AWARDS','AWARDS & HONORS','HONORS','PROJECTS','ADDITIONAL INFORMATION',
  'REFERENCES','PUBLICATIONS','RESEARCH','TRAINING','TRAINING & DEVELOPMENT','LEADERSHIP',
  'AFFILIATIONS','HOBBIES','HOBBIES & INTERESTS',
]);

function cleanContent(content: string): string {
  const lines = content.split('\n');
  while (lines.length > 0 && lines[lines.length - 1].trim() === '') lines.pop();
  while (lines.length > 0) {
    const last = lines[lines.length - 1].trim();
    const upper = last.toUpperCase();
    if (
      last.length > 0 && last.length < 45 &&
      (KNOWN_SECTION_HEADERS.has(upper) || (last === upper && /^[A-Z][A-Z\s&/\-:]+$/.test(upper)))
    ) { lines.pop(); } else { break; }
  }
  return lines.join('\n').trim();
}

/** Build resume context so the LLM can produce unique, candidate-specific text. */
function buildResumeContext(sections: ResumeSection[], fullText?: string): ResumeContext {
  let candidateName: string | undefined;
  const personal = sections.find(s =>
    PERSONAL_SECTION_TITLES.has((s.title || '').toUpperCase().trim())
  );
  if (personal?.content) {
    const firstLine = personal.content.split('\n')[0]?.trim().slice(0, 120);
    if (firstLine && !/^[\d\s\-+@.\/():]+$/.test(firstLine)) candidateName = firstLine;
  }
  const summaryParts: string[] = sections.map(s => `${s.title}: ${(s.content || '').slice(0, 80).replace(/\n/g, ' ')}`);
  const fullResumeSummary = (fullText && fullText.length > 0)
    ? fullText.slice(0, 600).replace(/\s+/g, ' ').trim()
    : summaryParts.join(' | ').slice(0, 600);
  return {
    candidateName: candidateName || undefined,
    fullResumeSummary: fullResumeSummary || undefined,
  };
}

// ── Section state ────────────────────────────────────────────────────────────
type SectionStatus = 'loading' | 'ready' | 'typing' | 'done';

interface SectionState {
  title: string;
  originalContent: string;
  optimizedContent: string;
  status: SectionStatus;
}

// ── Props ────────────────────────────────────────────────────────────────────
interface ResumeDataLike {
  parsedText: string;
  sections?: ResumeSection[];
}

interface ProcessingStepProps {
  resumeData?: ResumeDataLike | null;
  jobData: Record<string, unknown> | null | undefined;
  onComplete: (sections: Array<{ title: string; content: string }>) => void;
  onCancel: () => void;
  onRetry?: () => void;
  flowError?: string | null;
  /** Legacy prop — kept for backward compat but not used for optimization logic */
  optimizationHook?: unknown;
}

// ── Component ────────────────────────────────────────────────────────────────
const ProcessingStep: React.FC<ProcessingStepProps> = ({
  resumeData,
  jobData,
  onComplete,
  onCancel,
  onRetry,
  flowError,
}) => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const abortRef = useRef<AbortController | null>(null);
  const filmstripRef = useRef<HTMLDivElement>(null);
  const typewriterEndRef = useRef<HTMLSpanElement>(null);
  const isVisibleRef = useRef(true);

  const [sections, setSections] = useState<SectionState[]>([]);
  const [typewriterIdx, setTypewriterIdx] = useState(0);
  const [typewriterLen, setTypewriterLen] = useState(0);
  const [error, setError] = useState<string | null>(flowError || null);
  const [isStarted, setIsStarted] = useState(false);

  // Pause typewriter when tab is hidden
  useEffect(() => {
    const fn = () => { isVisibleRef.current = !document.hidden; };
    document.addEventListener('visibilitychange', fn);
    return () => document.removeEventListener('visibilitychange', fn);
  }, []);

  // ── Parse original sections from resumeData ───────────────────────────────
  // IMPORTANT: do NOT run bucketResumeSections() here.
  // By the time sections reach ProcessingStep they have already been through
  // parseResumeSections → arrangeResumeSections → bucketResumeSections in
  // ParsedResumeStep. Running bucketResumeSections / redistributeMisplacedContent
  // a second time scrambles already-correct section assignments and sends
  // corrupted content to the LLM, producing a broken optimized resume.
  const getOriginalSections = useCallback((): ResumeSection[] => {
    if (!resumeData) return [];
    if (Array.isArray(resumeData.sections) && resumeData.sections.length > 0) {
      // Sections are already clean from ParsedResumeStep — just sort canonically.
      return sortSectionsByCanonicalOrder(resumeData.sections as ResumeSection[]);
    }
    if (resumeData.parsedText) {
      // Fallback: raw text was never through ParsedResumeStep — parse once only.
      const parsed = parseResumeSections(normalizeResumeText(resumeData.parsedText));
      return sortSectionsByCanonicalOrder(normalizeSectionTitles(parsed));
    }
    return [];
  }, [resumeData]);

  // ── Start all section optimizations concurrently ──────────────────────────
  useEffect(() => {
    if (isStarted) return;
    setIsStarted(true);

    const originalSections = getOriginalSections();
    if (originalSections.length === 0) {
      setError('No resume sections found. Please go back and re-upload your resume.');
      return;
    }

    // Initialise section state — all loading
    const initial: SectionState[] = originalSections.map(s => ({
      title: s.title,
      originalContent: s.content || '',
      optimizedContent: '',
      status: 'loading',
    }));
    setSections(initial);

    const job: JobContext = {
      title: (jobData as Record<string, unknown>)?.title as string | undefined,
      company: (jobData as Record<string, unknown>)?.company as string | undefined,
      description: (jobData as Record<string, unknown>)?.description as string | undefined,
      skills: (jobData as Record<string, unknown>)?.skills as string[] | undefined,
      requirements: (jobData as Record<string, unknown>)?.requirements as string[] | undefined,
    };

    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    const resumeContext = buildResumeContext(
      originalSections,
      (resumeData as ResumeDataLike)?.parsedText
    );

    // Fire all section calls concurrently; update state as each returns
    originalSections.forEach((sec, idx) => {
      optimizeSection({ title: sec.title, content: sec.content || '' }, job, signal, resumeContext)
        .then(result => {
          setSections(prev => {
            const next = [...prev];
            next[idx] = {
              ...next[idx],
              optimizedContent: cleanContent(result.optimizedContent),
              status: 'ready',
            };
            return next;
          });
        })
        .catch(err => {
          if ((err as Error).name === 'AbortError') return;
          // On error, mark as ready with original content
          setSections(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], optimizedContent: next[idx].originalContent, status: 'ready' };
            return next;
          });
        });
    });

    return () => {
      abortRef.current?.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Typewriter: advance through sections in order ─────────────────────────
  const currentSection = sections[typewriterIdx];
  const currentContent = currentSection?.optimizedContent || '';
  const isCurrentReady = currentSection?.status === 'ready' || currentSection?.status === 'typing';
  const typewriterComplete = typewriterIdx >= sections.length;

  // When current section becomes ready, start typing it
  useEffect(() => {
    if (!isCurrentReady || typewriterComplete) return;
    setSections(prev => {
      const next = [...prev];
      if (next[typewriterIdx]?.status === 'ready') next[typewriterIdx] = { ...next[typewriterIdx], status: 'typing' };
      return next;
    });
    setTypewriterLen(0);
  }, [typewriterIdx, isCurrentReady, typewriterComplete]);

  // Typewriter interval — only runs when there is content to type
  useEffect(() => {
    if (typewriterComplete || !isCurrentReady || !currentContent) return;
    const id = setInterval(() => {
      if (!isVisibleRef.current) return;
      setTypewriterLen(len => {
        if (len >= currentContent.length) return len;
        return Math.min(len + TYPEWRITER_CHARS_PER_TICK, currentContent.length);
      });
    }, TYPEWRITER_TICK_MS);
    return () => clearInterval(id);
  }, [typewriterIdx, isCurrentReady, currentContent, typewriterComplete]);

  // Advance to next section when current is fully typed.
  // Handles BOTH normal content AND empty/short content so we never get stuck.
  useEffect(() => {
    if (typewriterComplete || !isCurrentReady) return;
    // Empty content: skip immediately without requiring length > 0
    const shouldAdvance =
      currentContent.length === 0 ||
      (typewriterLen >= currentContent.length && currentContent.length > 0);
    if (!shouldAdvance) return;
    setSections(prev => {
      const next = [...prev];
      if (next[typewriterIdx]) next[typewriterIdx] = { ...next[typewriterIdx], status: 'done' };
      return next;
    });
    setTypewriterIdx(i => i + 1);
    setTypewriterLen(0);
  }, [typewriterLen, currentContent, typewriterIdx, isCurrentReady, typewriterComplete]);

  // Scroll to the active card when the section index advances.
  // Query by data-idx so this works even before the card reaches 'typing' status.
  useEffect(() => {
    if (!filmstripRef.current) return;
    const el = filmstripRef.current.querySelector<HTMLElement>(`[data-idx="${typewriterIdx}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [typewriterIdx]);

  // As the typewriter types, keep the growing text end visible.
  // typewriterEndRef is placed at the cursor inside the active card.
  useEffect(() => {
    if (!typewriterEndRef.current) return;
    if (typewriterLen % 20 !== 0) return;
    typewriterEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [typewriterLen]);

  // ── Loading state: not started yet ────────────────────────────────────────
  const allSectionsLoaded = sections.length > 0;
  const readyCount = sections.filter(s => s.status !== 'loading').length;
  const totalCount = sections.length;

  // ── Assemble result and call onComplete ───────────────────────────────────
  const handleViewResults = useCallback(() => {
    const result = sections.map(s => ({
      title: s.title,
      content: s.optimizedContent || s.originalContent,
    }));
    onComplete(result);
  }, [sections, onComplete]);

  // ── Error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex flex-col flex-1 min-h-0 w-full bg-gray-50 dark:bg-gray-900 items-center justify-center p-8">
        <div className="rounded-2xl border border-red-200 bg-gray-100 p-6 dark:border-red-800 dark:bg-gray-800/90 max-w-md w-full">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-700 dark:text-red-300 text-sm mb-1">{t('optimizer.processingStep.optimizationError')}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button type="button" onClick={onCancel} className="flex-1 min-h-[40px] px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              ← {t('optimizer.processingStep.backShort')}
            </button>
            {onRetry && (
              <button type="button" onClick={onRetry} className={`flex-1 min-h-[40px] rounded-xl px-4 py-2 text-sm font-semibold ${STEP_PRIMARY_CTA_CLASS}`}>
                {t('optimizer.processingStep.retryShort')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const jobTitle = (jobData as Record<string,unknown>)?.title as string || 'target role';
  const progressPct = totalCount > 0 ? Math.round((readyCount / totalCount) * 100) : 0;

  // ── Shared section cards (same for native and web) ────────────────────────
  const renderSectionCards = () => (
    !allSectionsLoaded ? (
      <div className="space-y-3">
        {[90, 80, 95, 70, 65].map((w, i) => (
          <div
          key={i}
          className={`rounded-2xl p-4 animate-pulse ${isNative ? '' : 'border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800'}`}
          style={isNative ? { background: 'var(--ggj-bg-card)', border: '1px solid var(--ggj-border)' } : undefined}
        >
            <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 mb-3" style={{ width: `${w * 0.4}%` }} />
            <div className="space-y-1.5">
              {[w, w - 15, w - 8].map((ww, li) => (
                <div key={li} className="h-2 rounded bg-gray-100 dark:bg-gray-700" style={{ width: `${ww}%` }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="space-y-3">
        {sections.map((sec, i) => {
          const isActiveTyping = i === typewriterIdx && sec.status === 'typing';
          const isDone = sec.status === 'done' || (typewriterComplete && i < sections.length);
          const isLoading = sec.status === 'loading';

          return (
            <motion.div
              key={`${sec.title}-${i}`}
              data-idx={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03, duration: 0.2 }}
              className={`rounded-2xl overflow-hidden transition-colors ${isNative ? '' : 'border bg-gray-100 dark:bg-gray-800'} ${
                isActiveTyping
                  ? isNative ? '' : 'border-[#6C47FF]/30 shadow-[0_0_0_2px_rgba(108,71,255,0.08)]'
                  : isDone
                    ? isNative ? '' : 'border-emerald-200 dark:border-emerald-800/40'
                    : isNative ? '' : 'border-gray-200 dark:border-gray-700'
              }`}
              style={isNative ? {
                background: 'var(--ggj-bg-card)',
                border: isActiveTyping
                  ? '1px solid var(--brand-accent-border)'
                  : isDone
                  ? '1px solid rgba(34,197,94,0.25)'
                  : '1px solid var(--ggj-border)',
                boxShadow: isActiveTyping ? '0 0 0 2px var(--brand-accent-bg)' : 'var(--ggj-shadow-card)',
              } : undefined}
            >
              {/* Section title bar */}
              <div               className="px-4 py-2.5 border-b flex items-center gap-2.5"
              style={isNative ? {
                borderColor: isActiveTyping ? 'rgba(108,71,255,0.15)' : isDone ? 'rgba(34,197,94,0.2)' : 'var(--ggj-border)',
                background: isActiveTyping ? 'rgba(108,71,255,0.04)' : isDone ? 'rgba(34,197,94,0.06)' : 'var(--ggj-bg-card-inner)',
              } : undefined}>
                {isDone ? (
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                ) : isActiveTyping ? (
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-[#6C47FF]/40 border-t-[#6C47FF] animate-spin shrink-0" />
                ) : isLoading ? (
                  <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin shrink-0" />
                ) : (
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-gray-200 dark:border-gray-600 shrink-0" />
                )}
                <span className={`text-xs font-bold uppercase tracking-wider ${
                  isActiveTyping ? 'text-[#6C47FF] dark:text-[#5B8DFF]'
                    : isDone ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {sec.title}
                </span>
                {isActiveTyping && (
                  <span className="ml-auto text-[11px] text-[#6C47FF]/70 font-semibold animate-pulse">{t('optimizer.processingStep.aiRewriting')}</span>
                )}
                {isLoading && (
                  <span className="ml-auto text-[11px] text-gray-400">{t('optimizer.processingStep.requesting')}</span>
                )}
              </div>

              {/* ACTIVE: before / after split */}
              {isActiveTyping && (
                <>
                  <div className="px-4 pt-3 pb-3 bg-gray-50/80 dark:bg-gray-900/50 border-b border-dashed border-gray-200 dark:border-gray-700">
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 px-2 py-0.5 bg-gray-200/60 rounded">
                      {t('optimizer.processingStep.original')}
                    </span>
                    <pre className="text-[13px] leading-relaxed whitespace-pre-wrap font-sans text-gray-400 dark:text-gray-500 break-words line-clamp-4">
                      {sec.originalContent || '(empty)'}
                    </pre>
                  </div>
                  <div className="px-4 pt-3 pb-4">
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-2 px-2 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 rounded">
                      {t('optimizer.processingStep.aiOptimized')}
                    </span>
                    <pre className="text-[14px] leading-relaxed whitespace-pre-wrap font-sans text-gray-700 dark:text-gray-200 break-words">
                      {currentContent.slice(0, typewriterLen)}
                      <span ref={typewriterEndRef} className="inline-block w-0.5 h-4 bg-[#6C47FF] animate-[blink_0.9s_step-end_infinite] ml-px align-middle" />
                    </pre>
                  </div>
                </>
              )}

              {/* DONE */}
              {isDone && !isActiveTyping && (
                <div className="px-4 py-4">
                  <pre className="text-[14px] leading-relaxed whitespace-pre-wrap font-sans text-gray-700 dark:text-gray-200 break-words">
                    {sec.optimizedContent || sec.originalContent}
                  </pre>
                </div>
              )}

              {/* LOADING / PENDING */}
              {!isActiveTyping && !isDone && (
                <div className="px-4 py-3 opacity-45">
                  {sec.originalContent ? (
                    <pre className="text-[13px] leading-relaxed whitespace-pre-wrap font-sans text-gray-500 dark:text-gray-400 break-words line-clamp-4">
                      {sec.originalContent}
                    </pre>
                  ) : (
                    <div className="space-y-1.5">
                      {[80, 65, 75].map((w, li) => (
                        <div key={li} className="h-2 rounded bg-gray-100 dark:bg-gray-700 animate-pulse" style={{ width: `${w}%` }} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    )
  );

  /* ─────────────────────────────────────────────────────────────────────────
     NATIVE MOBILE RENDER — splash screen with branded processing header
     ───────────────────────────────────────────────────────────────────────── */
  if (isNative) {
    return (
      /* absolute fill: bypasses parent overflow-y-auto so this component
         controls its own scroll internally */
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', isolation: 'isolate' as const }}>
        {/* ── Purple splash header (always purple — completion shown by pill, not color change) ── */}
        <div
          className="flex-shrink-0"
          style={{
            background: 'var(--ggj-header-bg)',
            paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
            borderRadius: '0 0 24px 24px',
          }}
        >
          {/* Top row */}
          <div className="flex items-center justify-between px-4 pb-3 gap-2">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ggj-header-btn-bg)' }}>
              {typewriterComplete
                ? <CheckCircle className="w-5 h-5" style={{ color: 'var(--ggj-header-text)' }} />
                : <Sparkles className="w-5 h-5 animate-pulse" style={{ color: 'var(--ggj-header-text)' }} />
              }
            </div>
            <div className="flex-1 min-w-0 px-2">
              <div className="text-[10px] font-semibold uppercase tracking-widest leading-none mb-0.5" style={{ color: 'var(--ggj-header-sub)' }}>
                {typewriterComplete ? 'Resume Optimizer' : 'AI Processing'}
              </div>
              <div className="text-sm font-bold leading-tight truncate" style={{ color: 'var(--ggj-header-text)' }}>
                {typewriterComplete
                  ? `Optimized — ${jobTitle}`
                  : allSectionsLoaded
                    ? `Optimizing for: ${jobTitle}`
                    : 'Analysing resume…'
                }
              </div>
            </div>
            {/* Cancel */}
            {!typewriterComplete && allSectionsLoaded && (
              <button
                type="button"
                onClick={() => { abortRef.current?.abort(); onCancel(); }}
                className="w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation flex-shrink-0"
                style={{ background: 'var(--ggj-header-btn-bg)', color: 'var(--ggj-header-btn-icon)' }}
                aria-label="Cancel optimization"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Progress bar + counters */}
          {allSectionsLoaded && (
            <div className="px-4 pb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs" style={{ color: 'var(--ggj-header-sub)' }}>
                  {typewriterComplete ? 'All sections rewritten' : `${readyCount} of ${totalCount} sections ready`}
                </span>
                <span className="text-xs font-bold" style={{ color: 'var(--ggj-header-text)' }}>{typewriterComplete ? '100%' : `${progressPct}%`}</span>
              </div>
              <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--ggj-header-btn-bg)' }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${typewriterComplete ? 100 : progressPct}%`,
                    background: 'var(--ggj-header-text)',
                    opacity: 0.85,
                  }}
                />
              </div>
              {/* Section pills */}
              {totalCount > 0 && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {sections.slice(0, 8).map((sec, i) => {
                    const isDone = sec.status === 'done' || (typewriterComplete && i < sections.length);
                    const isActive = i === typewriterIdx && sec.status === 'typing';
                    return (
                      <span
                        key={i}
                        className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${isActive ? 'animate-pulse' : ''}`}
                      style={{
                        background: isDone ? 'var(--ggj-header-btn-bg)' : isActive ? 'var(--ggj-header-text)' : 'rgba(0,0,0,0.08)',
                        color: isDone ? 'var(--ggj-header-text)' : isActive ? 'var(--ggj-accent-start)' : 'var(--ggj-header-sub)',
                      }}
                      >
                        {sec.title.length > 12 ? sec.title.slice(0, 11) + '…' : sec.title}
                      </span>
                    );
                  })}
                  {sections.length > 8 && (
                    <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--ggj-header-btn-bg)', color: 'var(--ggj-header-sub)' }}>
                      +{sections.length - 8}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Scrollable section cards (z-index 0 so sticky bar stays on top) ── */}
        <div
          ref={filmstripRef}
          className="flex-1 min-h-0 overflow-y-auto scrollbar-hide"
          style={{ background: 'var(--ggj-bg-page)', position: 'relative' as const, zIndex: 0 }}
        >
          <div className="px-4 pt-4 pb-4">
            {renderSectionCards()}
          </div>
        </div>

        {/* ── Bottom CTA (always on top of scroll) ── */}
        {typewriterComplete && sections.length > 0 && (
          <div
            className="flex-shrink-0 px-4 pt-3 border-t"
            style={{
              position: 'relative' as const,
              zIndex: 200,
              background: 'var(--ggj-bg-card)',
              borderColor: 'var(--ggj-border)',
              paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
            }}
          >
            <button
              type="button"
              onClick={handleViewResults}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-sm font-bold touch-manipulation"
              style={{
                background: 'var(--ggj-accent-gradient)',
                color: '#ffffff',
                boxShadow: 'var(--ggj-btn-shadow)',
              }}
            >
              <CheckCircle className="w-4 h-4 shrink-0" />
              View Results
              <ArrowRight className="w-4 h-4 shrink-0" />
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     WEB RENDER (unchanged)
     ───────────────────────────────────────────────────────────────────────── */
  return (
    <div className="flex flex-col flex-1 min-h-0 w-full bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-1 min-h-0 min-w-0">
        {/* ── Left sidebar ── */}
        <aside className="hidden md:flex w-72 shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-900/80">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              {typewriterComplete ? <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" /> : <span className="w-5 h-5 rounded-full border-2 border-[#6C47FF]/40 border-t-[#6C47FF] animate-spin shrink-0" />}
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">{typewriterComplete ? t('optimizer.processingStep.allSectionsRewritten') : t('optimizer.processingStep.aiRewriting')}</h3>
            </div>
            {!typewriterComplete && allSectionsLoaded && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{readyCount}/{totalCount} {t('optimizer.processingStep.sectionsReady', 'sections ready')}</p>}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto py-2 px-3 scrollbar-hide">
            {sections.map((sec, i) => {
              const isActive = i === typewriterIdx;
              const isDone = sec.status === 'done' || (typewriterComplete && i < sections.length);
              const isLoading = sec.status === 'loading';
              return (
                <div key={`sidebar-${sec.title}-${i}`} className={`flex items-center gap-2 py-2 px-2 rounded-lg text-left ${isActive ? 'bg-[#6C47FF]/10 dark:bg-[#6C47FF]/20' : ''}`}>
                  {isDone ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" /> : isActive && sec.status === 'typing' ? <span className="w-4 h-4 rounded-full border-2 border-[#6C47FF]/40 border-t-[#6C47FF] animate-spin shrink-0" /> : isLoading ? <Loader2 className="w-4 h-4 text-gray-400 animate-spin shrink-0" /> : <span className="w-4 h-4 rounded-full border-2 border-gray-200 dark:border-gray-600 shrink-0" />}
                  <span className={`text-xs font-medium truncate flex-1 min-w-0 ${isActive ? 'text-[#6C47FF] dark:text-[#5B8DFF]' : isDone ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400'}`}>{sec.title}</span>
                </div>
              );
            })}
          </div>
        </aside>

        {/* ── Scrollable section cards ── */}
        <div ref={filmstripRef} className="flex-1 min-h-0 overflow-y-auto py-5 px-4 scrollbar-hide">
          <div className="max-w-2xl mx-auto">
            <div className="mb-5 flex items-center gap-3">
              <div className={`${STEP_HEADER_ICON_CLASS} h-10 w-10 shadow-md`}>
                {typewriterComplete ? (
                  <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-300/40 border-t-indigo-500 dark:border-indigo-400/30 dark:border-t-indigo-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-bold text-gray-900 dark:text-white truncate">
                  [ {typewriterComplete ? t('optimizer.processingStep.optimizingFor', { title: jobTitle }) : allSectionsLoaded ? t('optimizer.processingStep.optimizingFor', { title: `${jobTitle} — ${readyCount}/${totalCount} sections ready` }) : t('optimizer.processingStep.analysingResume')} ]
                </h2>
                <p className="text-sm text-gray-400 dark:text-gray-500">{typewriterComplete ? t('optimizer.processingStep.allSectionsRewritten') : t('optimizer.processingStep.individualLlmCalls')}</p>
              </div>
            </div>
            {renderSectionCards()}
          </div>
        </div>
      </div>

      {typewriterComplete && sections.length > 0 && (
        <div className="flex-shrink-0 py-3 flex justify-end px-4">
          <button type="button" onClick={handleViewResults} className={`flex touch-manipulation items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-semibold shadow transition-all hover:opacity-90 active:scale-95 ${STEP_PRIMARY_CTA_CLASS}`}>
            {t('optimizer.processingStep.viewResults')}<ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {!typewriterComplete && allSectionsLoaded && (
        <div className="flex-shrink-0 py-3 flex justify-center px-4">
          <button type="button" onClick={() => { abortRef.current?.abort(); onCancel(); }} className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-800/50 text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors touch-manipulation">
            {t('optimizer.processingStep.cancelOptimization', 'Cancel Optimization')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProcessingStep;
